/**
 * SmartAdd tokenizer.
 *
 * Splits a free-text quick-capture string into a leading title and a sequence
 * of operator chunks. The grammar is intentionally line-noise free; see
 * tokenizer.test.ts for the canonical examples.
 */

/** Single-token operators consume one whitespace-delimited word. */
const SINGLE_TOKEN_OPS = new Set(['!', '#', '=', '/', '@', '%', '>', '+']);

/** Multi-token operators consume the rest of the line until the next operator. */
const MULTI_TOKEN_OPS = new Set(['^', '~', '*', '//']);

/** Every operator character. */
const ALL_OP_CHARS = new Set<string>([
  ...[...SINGLE_TOKEN_OPS, ...MULTI_TOKEN_OPS].flatMap((operator) => [...operator]),
]);

export type Operator = '^' | '~' | '!' | '#' | '=' | '*' | '/' | '//' | '@' | '%' | '>' | '+';

export type TextToken = { type: 'text'; value: string };
export type OpToken = { type: 'op'; operator: Operator; value: string };
export type Token = TextToken | OpToken;

const isWhitespace = (c: string | undefined): boolean => c === ' ' || c === '\t' || c === '\n';

/**
 * An operator only "fires" when it appears at the start of input or
 * immediately after whitespace. This is what lets "C# basics" stay as
 * literal text instead of being chopped at the `#`.
 */
function isOperatorBoundary(input: string, pos: number): boolean {
  if (pos >= input.length) return false;
  if (!ALL_OP_CHARS.has(input[pos] as string)) return false;
  if (pos === 0) return true;
  return isWhitespace(input[pos - 1]);
}

/** Skip whitespace, return the new position. */
function skipWhitespace(input: string, pos: number): number {
  let i = pos;
  while (i < input.length && isWhitespace(input[i])) i++;
  return i;
}

/** Read until the next operator boundary or end of input. */
function readUntilNextOp(input: string, start: number): { value: string; end: number } {
  let i = start;
  while (i < input.length && !isOperatorBoundary(input, i)) {
    i++;
  }
  // Trim trailing whitespace from the captured slice.
  let end = i;
  while (end > start && isWhitespace(input[end - 1])) end--;
  return { value: input.slice(start, end), end: i };
}

/** Read a single whitespace-delimited word. */
function readWord(input: string, start: number): { value: string; end: number } {
  let i = start;
  while (i < input.length && !isWhitespace(input[i]) && !isOperatorBoundary(input, i)) {
    i++;
  }
  return { value: input.slice(start, i), end: i };
}

export function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = skipWhitespace(input, 0);

  // Leading text (the title): everything up to the first operator boundary.
  if (i < input.length && !isOperatorBoundary(input, i)) {
    const { value, end } = readUntilNextOp(input, i);
    if (value.length > 0) {
      tokens.push({ type: 'text', value });
    }
    i = end;
  }

  // Operator stream.
  while (i < input.length) {
    i = skipWhitespace(input, i);
    if (i >= input.length) break;

    const ch = input[i] as string;
    if (!ALL_OP_CHARS.has(ch)) {
      // Free text mid-stream is ignored: skip until the next operator boundary.
      const { end } = readUntilNextOp(input, i);
      i = end;
      continue;
    }

    const operator = input.startsWith('//', i) ? '//' : (ch as Operator);
    i += operator.length; // consume the operator chars

    if (MULTI_TOKEN_OPS.has(operator)) {
      const { value, end } = readUntilNextOp(input, i);
      if (value.length > 0) {
        tokens.push({ type: 'op', operator, value });
      }
      i = end;
    } else {
      // Single-token operator.
      const { value, end } = readWord(input, i);
      if (value.length > 0) {
        tokens.push({ type: 'op', operator, value });
      }
      i = end;
    }
  }

  return tokens;
}
